import { Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import {
  AccountRegistrationService,
  LoaderService,
  MetricState,
  PatientState,
  selectCampaignTemplateId,
  selectPatientState,
} from '@patient-ui/patient-web/store';
import { regexPatterns } from '@patient-ui/shared/constants';
import { IToken, Patient } from '@patient-ui/shared/models';

import { MfaSetupConfirmationComponent } from '../../../containers/mfa-setup-confirmation/mfa-setup-confirmation.component';
import { LayoutService } from '../../../shared/services/layout.service';

@Component({
  selector: 'patient-ui-registration-email-verify',
  templateUrl: './registration-email-verify.component.html',
  styleUrls: ['./registration-email-verify.component.scss'],
})
export class RegistrationEmailVerifyComponent implements OnInit, OnDestroy {
  @HostBinding('class.expired-modal') get expiredModal() {
    return this.isOnetimeVerificationCodeExpired;
  }

  @Output() emailVerified: EventEmitter<boolean> = new EventEmitter();
  @Output() signOutSelected: EventEmitter<void> = new EventEmitter();
  @Output() modalHeaderChanged: EventEmitter<string> = new EventEmitter();

  verifyCodeForm!: UntypedFormGroup;
  loggedIn = false;
  patient: Patient | undefined;
  destroyed = new Subject<void>();
  proccessing$: Subject<boolean> = this.loaderService.isLoading;
  registeredEmail = '';
  token!: string;
  acttoken!: string;
  alertStatus!: 'primary' | 'success' | 'danger' | 'warning' | 'info';
  alertText!: string;
  codeResent = false;
  showErrorAlert = false;
  errorAlertText = '';
  infoText = '';
  isResendingCode = false;
  errorText =
    'The verification code you entered is incorrect. Please try again or click <strong>Resend Code</strong> to receive a new code.';
  errorTextLoggedIn =
    'The verification code you entered is incorrect. Please<br> try again or click <strong>Resend Code</strong> to receive a new code.';
  serviceErrText =
    '<strong>We cannot process your request at this time.</strong>';

  get expiredCodeText(): string {
    return (
      '<p>The code we sent to ' +
      this.registeredEmail +
      ' has expired. Click</p>' +
      '<p><strong>Resend Code</strong> to receive a new one. Your new code will be valid</p>' +
      '<p>for <strong>20 minutes</strong>.</p>'
    );
  }

  private get baseVerificationText(): string {
    return `<p>We've sent a verification code to <strong>${this.registeredEmail}</strong>.</p>`;
  }

  get resendCodeText(): string {
    return (
      this.baseVerificationText +
      '<p>Your new code is valid for 20 minutes.</p>'
    );
  }

  get displayTextForResendFlow(): string {
    // If code was actually resent (codeResent is true), show the full resend text with time validity
    // Otherwise, show just the email confirmation without time validity
    return this.codeResent ? this.resendCodeText : this.baseVerificationText;
  }

  campaignId = '';
  isOnetimeVerificationCodeExpired = false;
  codeResentAfterExpiry = false;

  readonly validationMessages = {
    pin: {
      required: 'Verification Code is required',
      pattern: 'Must contain at least 4 numeric characters',
    },
  };

  emailKey = '';
  emailValue = '';
  tokenKey = '';
  tokenValue = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private loaderService: LoaderService,
    private accountRegistrationService: AccountRegistrationService,
    private changeDetectorRef: ChangeDetectorRef,
    private patientStore: Store<PatientState>,
    private metricStore: Store<MetricState>,
    private layoutService: LayoutService,
    private modalService: NgbModal,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.initForm(); // Initialize form first to prevent FormGroup errors
    this.layoutService.setShowSignInButton(false);
    const currentUrl = window.location.href;
    this.sanitizeUrl(currentUrl);

    this.layoutService.setShowHeaderLinks(false);
    this.layoutService.setShowFooterAppButtons(true);
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    this.acttoken = this.route.snapshot.queryParamMap.get('acttoken') || '';
    this.registeredEmail = this.route.snapshot.queryParamMap.get('email') || '';
    const source = this.route.snapshot.queryParamMap.get('source') || '';
    if (source === 'AppointmentUI') {
      sessionStorage.setItem('createAccountCtaSource', 'AppointmentUI');
    }

    this.metricStore
      .select(selectCampaignTemplateId)
      .pipe(takeUntil(this.destroyed))
      .subscribe((campaign) => (this.campaignId = campaign || ''));

    // Top text for registration cases
    this.infoText =
      '<p><strong>Note: </strong> Do not close this window or click the Back button.</p><p>If you need to check your email, please open a new window. If you did not receive a verification code within a few minutes, check your spam folder or click <strong>Resend Code</strong>.</p>' +
      'The verification code is valid for 20 minutes.';

    if (this.acttoken !== '') {
      // duplicate registration case
      this.infoText +=
        '<p>If you do not complete this verification now, your account will not be created and <strong>' +
        this.registeredEmail +
        '</strong> cannot be used as a user ID.</p>';
      this.alertStatus = 'warning';
      this.alertText = this.infoText;
    } else if (this.token !== '') {
      // standard registration case
      this.alertStatus = 'info';
      this.alertText = this.infoText;
    } else {
      // this is a logged in user that hasn't been verified yet
      this.loggedIn = true;

      // Initialize alert to empty for logged-in users
      this.alertText = '';
      this.alertStatus = 'info';

      this.patientStore
        .select(selectPatientState)
        .pipe(take(1))
        .subscribe((patientState) => {
          this.patient = patientState.primaryAccountHolder;

          if (this.patient) {
            this.registeredEmail = this.patient.primaryEmailAddress.address;

            // Start with Modal 1: "Verify Your Email"
            // Only initialize if not already set (prevents going back from Modal 2 to Modal 1)
            if (!this.codeResentAfterExpiry) {
              this.isOnetimeVerificationCodeExpired = true;
              this.codeResentAfterExpiry = false;

              // Emit initial header
              this.modalHeaderChanged.emit('Verify Your Email');

              this.changeDetectorRef.detectChanges();
            }
          }
        });
    }
  }

  initForm() {
    this.verifyCodeForm = new UntypedFormGroup({
      verificationCode: new UntypedFormControl('', {
        validators: [
          Validators.required,
          Validators.pattern(regexPatterns.pin),
        ],
        updateOn: 'blur',
      }),
    });
  }

  ngOnDestroy(): void {
    this.layoutService.resetDefaults();
    this.destroyed.next();
    this.destroyed.complete();
  }

  onSubmit(): void {
    this.showErrorAlert = false;

    // Clear any previous alerts
    if (this.loggedIn) {
      this.alertText = '';
    }

    if (this.verifyCodeForm.invalid) {
      this.verifyCodeForm.controls.verificationCode.markAsDirty();
      return;
    }

    this.codeResent = false;

    const verifyCode = this.verifyCodeForm.controls.verificationCode
      .value as string;

    // Basic registration path
    if (this.token !== '') {
      this.accountRegistrationService
        .patientVerifyEmail(verifyCode, this.token, this.campaignId)
        .subscribe(
          (response) => {
            //Next callback
            console.log(response);
            this.router.navigate(['account/registration/success']);
          },
          (error) => {
            //Error callback
            console.log(error);
            this.showErrorAlert = true;
            this.errorAlertText = this.errorText;
            this.changeDetectorRef.detectChanges();
            window.scrollTo(0, 0);
          }
        );
      // Duplicate account - create new account with different email - path
    } else if (this.acttoken !== '') {
      this.accountRegistrationService
        .activateAccount(verifyCode, this.acttoken)
        .subscribe(
          (response) => {
            const params: Record<string, string> = {};

            //Next callback
            if (response.isQuizRequired) {
              params.token = response.token;
              this.router.navigate(['account/identity-verification'], {
                queryParams: params,
              });
            } else {
              this.router.navigate(['account/registration/success'], {
                queryParams: params,
              });
            }
          },
          (error) => {
            //Error callback
            console.log(error);
            if (error.status >= 400 && error.status < 500) {
              this.showErrorAlert = true;
              this.errorAlertText = this.errorText;
              window.scrollTo(0, 0);
              this.changeDetectorRef.detectChanges();
            } else {
              this.router.navigate(['account/registration/unable-to-register']);
            }
          }
        );
      // Loged in user but not verified yet path
    } else if (this.loggedIn) {
      this.accountRegistrationService
        .currentPatientVerifyEmail(verifyCode, this.campaignId)
        .subscribe(
          (response) => {
            //Next callback
            console.log(response);
            // TODO: What else to do here??
            this.emailVerified.emit(true);
            this.modalService.open(MfaSetupConfirmationComponent, {
              backdrop: 'static',
              keyboard: false,
            });
          },
          (error) => {
            //Error callback
            console.log(error);

            // Unified error handling - different messages based on modal state
            this.alertStatus = 'danger';
            if (
              error.error &&
              error.error.code === 'VerificationCode.Expired'
            ) {
              this.alertText =
                'The verification code has expired. Please select <strong>Send Code</strong> to receive a new one.';
            } else if (error.status >= 400 && error.status < 500) {
              // Different error message based on which modal we're on
              if (
                this.isOnetimeVerificationCodeExpired &&
                !this.codeResentAfterExpiry
              ) {
                // Modal 1: "Send Code"
                this.alertText =
                  'The verification code you entered is incorrect. Please try again or click <strong>Send Code</strong> to receive a new code.';
              } else {
                // Modal 2: "Resend Code"
                this.alertText =
                  'The verification code you entered is incorrect. Please try again or click <strong>Resend Code</strong> to receive a new code.';
              }
            } else {
              this.alertText =
                '<strong>We cannot process your request at this time.</strong>';
            }

            this.changeDetectorRef.detectChanges();
            window.scrollTo(0, 0);
          }
        );
    }
  }

  resendCode(): void {
    this.showErrorAlert = false;
    this.verifyCodeForm.controls['verificationCode'].reset();

    if (this.loggedIn) {
      // Clear alerts for logged-in modal transition
      this.alertText = '';
      this.alertStatus = 'info';
      // Show loading indicator
      this.isResendingCode = true;

      this.accountRegistrationService.currentPatientResendCode().subscribe(
        (response) => {
          // Hide loading indicator
          this.isResendingCode = false;

          // Transition to Modal 2: "Enter Verification Code"
          this.codeResent = true;
          this.isOnetimeVerificationCodeExpired = false;
          this.codeResentAfterExpiry = true;

          // Update header to Modal 2
          this.modalHeaderChanged.emit('Enter Verification Code');

          // Clear any previous alerts
          this.alertText = '';
          this.alertStatus = 'info';
          this.codeResentAfterExpiry = true; // Use second modal layout
          this.changeDetectorRef.detectChanges();
          console.log(response);
        },
        (error) => {
          // Hide loading indicator on error
          this.isResendingCode = false;
          //Error callback
          console.log(error);
        }
      );
    } else {
      // Non-logged-in: standard registration or duplicate account
      // Keep alertText as infoText (don't clear it)
      this.accountRegistrationService
        .accountResendCode({
          mobilePhoneNumber: '',
          token: this.token !== '' ? this.token : this.acttoken,
        } as IToken)
        .subscribe(
          (response) => {
            //Next callback
            this.codeResent = true;
            console.log(
              response.token ? 'Resent code.' : "Something isn't right."
            );
          },
          (error) => {
            //Error callback
            console.log(error);
          }
        );
    }
  }

  signOut($event?: Event): void {
    if ($event) {
      $event.preventDefault();
    }
    this.signOutSelected.emit();
  }

  sanitizeUrl(url: string) {
    // Remove leading/trailing whitespace
    let sanitizedUrl = url.trim();

    // Ensure the URL starts with a valid protocol
    if (
      !sanitizedUrl.startsWith('http://') &&
      !sanitizedUrl.startsWith('https://')
    ) {
      sanitizedUrl = 'https://' + sanitizedUrl;
    }

    // Parse the URL
    const parsedUrl = new URL(sanitizedUrl);

    const queryParam = new URLSearchParams(parsedUrl.search);

    // Sanitize the hostname
    parsedUrl.hostname = parsedUrl.hostname.toLowerCase();
    const origin = parsedUrl.pathname + `?`;

    // Sanitize the path
    parsedUrl.pathname = parsedUrl.pathname.replace(/[^a-zA-Z0-9-_/.]/g, '');

    queryParam.forEach((value, key) => {
      if (key.valueOf() === 'email') {
        this.emailKey = key.replace(regexPatterns.santizeKeyValue, '');
        this.emailValue = value.replace(regexPatterns.santizeEmail, '');

        parsedUrl.searchParams.delete(key);
        parsedUrl.searchParams.append(this.emailKey, this.emailValue);
      } else {
        this.tokenKey = key.replace(regexPatterns.santizeKeyValue, '');
        this.tokenValue = value.replace(regexPatterns.santizeKeyValue, '');

        parsedUrl.searchParams.delete(key);
        parsedUrl.searchParams.append(this.tokenKey, this.tokenValue);
      }
    });

    const urlConstructed = `${origin}${this.tokenKey}=${this.tokenValue}&${this.emailKey}=${this.emailValue}`;
    // Return the sanitized URL as a string
    this.location.replaceState(urlConstructed);
  }
}
